/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { Outlet } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { IndustryWithCompanies, useCompanies } from "services/hooks/useCompanies";
import Companies from "components/Companies";

/**
 * Distributes companies across rows based on device type
 * @param {Array} industries - Array of industry objects with companyCount and order fields
 * @param {string} deviceType - Device type: 'desktop', 'iPadPortrait', 'iPadLandscape', or 'mobile'
 * @returns {Array} Array of rows, where each row is an array of industry objects
 */
function distributeCompanies(industries: any[], deviceType: string): any[][] {
  if (!industries || industries.length === 0) {
    return [];
  }

  // Deduplicate industries first by creating a map using industry ID or name
  const uniqueIndustries = new Map();
  industries.forEach((industry) => {
    // Use a unique identifier for each industry (adjust as needed)
    const key = industry.id || industry.name;
    if (!uniqueIndustries.has(key)) {
      uniqueIndustries.set(key, industry);
    }
  });

  // Convert back to array and sort by order
  const sortedIndustries = Array.from(uniqueIndustries.values()).sort(
    (a, b) => a.order - b.order
  );

  // Set maximum companies per row based on device type
  const maxPerRow =
    {
      desktop: 5,
      iPadPortrait: 2,
      iPadLandscape: 3,
      mobile: 1,
    }[deviceType] || 5; // Default to desktop if deviceType is not recognized

  const rows: any[][] = [];
  let currentRow: any[] = [];
  let currentRowCapacity = maxPerRow;

  // Process each industry
  for (let i = 0; i < sortedIndustries.length; i++) {
    const industry = sortedIndustries[i];

    // Check if we need to start a new row because current row is full
    if (currentRowCapacity === 0) {
      rows.push([...currentRow]);
      currentRow = [];
      currentRowCapacity = maxPerRow;
    }

    // If industry fits in current row
    if (industry.companyCount <= currentRowCapacity) {
      currentRow.push(industry);
      currentRowCapacity -= industry.companyCount;

      // If this is the last industry, add the current row to rows
      if (i === sortedIndustries.length - 1 && currentRow.length > 0) {
        rows.push([...currentRow]);
      }
    } else {
      // If the current industry doesn't fit, try to find an industry that does
      let foundMatch = false;

      for (let j = i + 1; j < sortedIndustries.length; j++) {
        if (sortedIndustries[j].companyCount <= currentRowCapacity) {
          // Found a match, swap positions
          const temp = sortedIndustries[i];
          sortedIndustries[i] = sortedIndustries[j];
          sortedIndustries[j] = temp;

          // Add the fitting industry to current row
          currentRow.push(sortedIndustries[i]);
          currentRowCapacity -= sortedIndustries[i].companyCount;
          foundMatch = true;
          break;
        }
      }

      // If no match found, save current row and start a new one
      if (!foundMatch) {
        // Add current row to rows if not empty
        if (currentRow.length > 0) {
          rows.push([...currentRow]);
        }

        // Start a new row with the current industry
        currentRow = [industry];
        currentRowCapacity = maxPerRow - industry.companyCount;

        // If this is the last industry, add the current row to rows
        if (i === sortedIndustries.length - 1) {
          rows.push([...currentRow]);
        }
      }
    }
  }

  // Make sure to add the last row if it's not empty and wasn't added yet
  if (
    currentRow.length > 0 &&
    (rows.length === 0 || rows[rows.length - 1][0] !== currentRow[0])
  ) {
    rows.push([...currentRow]);
  }

  return rows;
}

const Home = () => {
  const [industriesRows, setIndustriesRows] = useState<
    IndustryWithCompanies[][]
  >([]);
  const { data: industries, isLoading } = useCompanies();

  // Media queries for responsive design
  const isDesktop = useMediaQuery("(min-width: 1367px)");
  const isIpadLandscape = useMediaQuery(
    "(min-width: 1024px) and (max-width: 1366px) and (orientation: landscape)"
  );
  const isIpadPortrait = useMediaQuery(
    "(min-width: 1024px) and (max-width: 1366px) and (orientation: portrait)"
  );
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Determine device type using useMemo
  const deviceType = useMemo(() => {
    if (isDesktop) return "desktop";
    if (isIpadLandscape) return "iPadLandscape";
    if (isIpadPortrait) return "iPadPortrait";
    if (isMobile) return "mobile";
    return "desktop"; // Default fallback
  }, [isDesktop, isIpadLandscape, isIpadPortrait, isMobile]);

  // Process the industries into rows when data changes or device type changes
  useEffect(() => {
    if (!isLoading && industries && industries.length > 0) {
      const rows = distributeCompanies(industries, deviceType);
      setIndustriesRows(rows);
    } else {
      setIndustriesRows([]); // Set empty array when industries not available
    }
  }, [isLoading]);

  return (
    <Box position="relative" bottom={0} width="100%">
      <Companies industriesRows={industriesRows} />
      <Outlet />
    </Box>
  );
};

export default Home;
